import { Box, TextField, Autocomplete } from '@mui/material';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { WrappedCareTeam } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import { CareTeamMemberGridRowData } from 'src/@types/crs/case';
import CustomModal, {
  CustomModalBasicProps,
  GridItem,
  GridSection,
} from 'src/components/CustomModal';
import DatePickerMoment from 'src/components/DatePickerMoment';
import InfoRibbon, { InfoTypography } from 'src/components/InfoRibbon';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useObjectState from 'src/hooks/useObjectState';
import { updateMemberPeriod } from '../service/careTeam';
import { checkIfDateIsAfterAnother } from 'src/utils/dates';
import { capitalize } from 'src/utils/string';
import { useValueSets } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';

interface CareTeamMemberEditModalProps extends CustomModalBasicProps {
  careTeamMember: CareTeamMemberGridRowData | null;
  careTeam: WrappedCareTeam;
  onMemberUpdateSuccess: Function;
}

interface CareTeamMemberFormState {
  isLoading: boolean;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  startDateError: string | null;
  selectedRole: ValueSetComposeIncludeConcept | null;
}

const getInitialFormState = (
  careTeamMember: CareTeamMemberGridRowData | null,
  roles: ValueSetComposeIncludeConcept[]
): CareTeamMemberFormState => ({
  isLoading: false,
  startDate: careTeamMember?.startDateObj ?? null,
  endDate: careTeamMember?.endDateObj ?? null,
  startDateError: null,
  selectedRole: roles?.find((r) => r.display === careTeamMember?.role) ?? null,
});

const CareTeamMemberEditModal = ({
  open,
  onClose,
  careTeamMember,
  careTeam,
  onMemberUpdateSuccess,
}: CareTeamMemberEditModalProps) => {
  
  const { i18n } = useLocales();

  // Fetch available roles from ValueSet
  const [rolesRecords] = useValueSets({
    filter: {
      identifier: 'ph-roles-codes',
    },
    map: ValueSetWrapper,
  });

  const roles = useMemo(() => rolesRecords?.[0]?.asListAll() ?? [], [rolesRecords]);

  const [{ isLoading, startDate, endDate, startDateError, selectedRole }, updateState] =
    useObjectState<CareTeamMemberFormState>({ ...getInitialFormState(careTeamMember, roles) });

  useEffect(() => {
    if (!open) return;
    updateState({ ...getInitialFormState(careTeamMember, roles) });
  }, [open, careTeamMember, roles]);

  const { enqueueSnackbar } = useSnackbar();

  const handleOnSave = async () => {
    if (checkIfDateIsAfterAnother(startDate, endDate)) {
      updateState({ startDateError: " Start Date can't be after End Date" });
      return;
    }
    updateState({ startDateError: null });
    const response = await updateMemberPeriod(
      {
        id: careTeamMember?.id,
        resourceType: careTeamMember?.memberType,
        startDate: startDate?.isValid?.() ? startDate?.toISOString() : null,
        endDate: endDate?.isValid?.() ? endDate?.toISOString() : null,
        role: selectedRole?.display ?? null,
        roleId: selectedRole?.code ?? null,
      },
      careTeam.id as string
    );
    if (!response) {
      enqueueSnackbar("There's been an error. Please Try Again", { variant: 'error' });
      return;
    }
    enqueueSnackbar(`${i18n('patients.details.careteams.message', 'crs')} Member succesfully updated`);
    return true;
  };

  const title = [
    i18n('case.details.careteams.titleModal', 'crs'),
    i18n('case.details.careteams.addNewMemberModal', 'crs')
  ];

  return (
    <CustomModal
      open={open}
      title={title[1]}
      breadcrumbs={title}
      onSave={async () => {
        updateState({ isLoading: true });
        const wasSaved = await handleOnSave();
        updateState({ isLoading: false });
        if (!wasSaved) return;
        onMemberUpdateSuccess();
        onClose?.({}, 'backdropClick');
      }}
      onClose={onClose}
      onCancel={onClose as Function}
      isLoading={isLoading}
      containerSx={[{ width: '70vw' }]}
      childrenWithoutPadding={
        <Box mt={3}>
          <InfoRibbon containerSx={{ marginTop: 2 }}>
            <InfoTypography>{i18n('patients.details.careteams.message', 'crs')}: {careTeam?.name ?? ''}</InfoTypography>
            <InfoTypography>Category: {careTeam?.plainCategory ?? ''}</InfoTypography>
            <InfoTypography>
              Managing Organization: {careTeam?.managingOrganizationName ?? ''}
            </InfoTypography>
            <InfoTypography>
              Status: {capitalize(careTeam?.severityStatus?.message ?? '')}
            </InfoTypography>
          </InfoRibbon>
        </Box>
      }
    >
      <>
        <GridSection>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Identifier'}
              defaultValue={careTeamMember?.identifier}
              type="text"
              placeholder="Identifier"
              variant="outlined"
              disabled
            />
          </GridItem>
        </GridSection>
        <GridSection>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Name'}
              defaultValue={careTeamMember?.name}
              type="text"
              placeholder="Name"
              variant="outlined"
              disabled
            />
          </GridItem>
        </GridSection>
        <GridSection>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Member Type'}
              defaultValue={careTeamMember?.memberType}
              type="text"
              placeholder="Member Type"
              variant="outlined"
              disabled
            />
          </GridItem>
        </GridSection>
        <GridSection>
          <GridItem xs={12}>
            <TextField
              fullWidth
              label={'Managing Organization'}
              defaultValue={careTeamMember?.associatedOrg}
              type="text"
              placeholder="Managing Organization"
              variant="outlined"
              disabled
            />
          </GridItem>
        </GridSection>
        <GridSection>
          <GridItem xs={12}>
            <Autocomplete
              fullWidth
              options={roles}
              value={selectedRole}
              onChange={(_, newValue) => {
                updateState({ selectedRole: newValue });
              }}
              getOptionLabel={(option) => option?.display ?? ''}
              isOptionEqualToValue={(option, value) => option?.code === value?.code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Role"
                  placeholder="Select a role"
                  variant="outlined"
                />
              )}
              disabled={isLoading}
            />
          </GridItem>
        </GridSection>
        <GridSection>
          <GridItem xs={5.9}>
            <DatePickerMoment
              value={startDate}
              error={startDateError}
              disabled={isLoading}
              label={'Start Date'}
              onChange={(value) => {
                updateState({
                  startDate: value,
                });
              }}
            />
          </GridItem>
          <GridItem xs={5.9}>
            <DatePickerMoment
              value={endDate}
              disabled={isLoading}
              label={'End Date'}
              onChange={(value) => {
                updateState({
                  endDate: value,
                });
              }}
            />
          </GridItem>
        </GridSection>
      </>
    </CustomModal>
  );
};

export default CareTeamMemberEditModal;
